import React from 'react';
import {
  Platform,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';

import { colors, layout, spacing } from '../theme';

/**
 * The gray column bar from the Requests list. Other lists pass their own
 * column widths as children so the bar, type, and padding stay one component.
 */
type ListColumnHeaderProps = {
  children: React.ReactNode;
  testID?: string;
  columnGap?: number;
  style?: StyleProp<ViewStyle>;
};

type ListColumnLabelProps = {
  children: React.ReactNode;
  testID?: string;
  style?: StyleProp<TextStyle>;
};

export function ListColumnHeader({
  children,
  testID,
  columnGap = layout.viewRequests.tableColumnGap,
  style,
}: ListColumnHeaderProps): React.ReactElement {
  return (
    <View style={[styles.bar, style]}>
      <View testID={testID} style={[styles.row, gapStyle(columnGap)]}>
        {children}
      </View>
    </View>
  );
}

export function ListColumnLabel({
  children,
  testID,
  style,
}: ListColumnLabelProps): React.ReactElement {
  return (
    <Text testID={testID} style={[styles.label, style]}>
      {children}
    </Text>
  );
}

function gapStyle(columnGap: number): ViewStyle {
  if (Platform.OS === 'web') {
    return { gap: columnGap, columnGap } as ViewStyle;
  }
  return { gap: columnGap };
}

const styles = StyleSheet.create({
  bar: {
    backgroundColor: colors.surfaceTableHeader,
    paddingVertical: layout.viewRequests.tablePaddingVertical,
    paddingHorizontal: layout.viewRequests.tablePaddingHorizontal,
    marginBottom: spacing.md,
    borderRadius: 4,
    shadowColor: colors.shadowBase,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.textPrimary,
    textAlign: 'left',
    flexShrink: 0,
    ...Platform.select({
      web: {
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    }),
  },
});
